import React, { Component } from 'react';
import fixEmailImage from 'app/common/images/fix_email.png';
import i18n from 'app/common/lib/i18n';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import PropTypes from 'prop-types';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { Box, Button, Checkbox, Image, Label, Text, TextField, Touchable } from 'gestalt';

const STEP = {
  FIRST: 'first',
  END: 'end',
};

export class FixEmailModal extends Component {
  static propTypes = {
    currentEmail: PropTypes.string.isRequired,
    handleComplete: PropTypes.func.isRequired,
    handleDismiss: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      currentStep: STEP.FIRST,
      newEmail: props.currentEmail,
      checked: true,
    };
  }

  onCheck = ({ checked }) => this.setState({ checked });

  onTextChange = ({ value: text }) => this.setState({ newEmail: text });

  getModalText(currentStep) {
    if (currentStep === STEP.FIRST) {
      return i18n._(
        "We're having some trouble reaching you. Is this still your email?",
        'prompt to update email',
      );
    } else {
      return i18n._(
        'Thanks! Your email preferences have been saved.',
        'after updating email address',
      );
    }
  }

  closeModal = () => {
    const { handleComplete } = this.props;
    setTimeout(handleComplete, 5000); // Short delay for user to see final page
  };

  updateEmailAddress = () => {
    const { currentEmail } = this.props;
    const { checked, newEmail } = this.state;

    if (currentEmail && newEmail && currentEmail.trim() !== newEmail) {
      ResourceFactory.create('UserSettingsResource', { email: newEmail, email_bounced: false })
        .callUpdate({ showError: false })
        .then(
          () => this.updateNotifSetting(checked),
          (error) => this.setState({ errorMessage: error.message }),
        );
    } else {
      // If the user clicks "submit" with the same email address, we assume they're confirming it's correct so just
      // proceed with the next step.
      this.updateNotifSetting(checked);
    }
  };

  updateNotifSetting = (newSetting) => {
    this.setState({ currentStep: STEP.END });

    let resourceParams;
    if (newSetting === true) {
      resourceParams = {
        section: 'settings_email_everything',
        option: 'ONLY_REQUIRED',
        value: false,
      };
    } else {
      resourceParams = {
        section: 'settings_email_everything',
        option: 'ONLY_REQUIRED',
        value: true,
      };
    }
    ResourceFactory.create('EmailSettingsResource', resourceParams).callUpdate();
    this.closeModal();
  };

  renderStep(currentStep) {
    const { checked, errorMessage, newEmail } = this.state;

    if (currentStep === STEP.FIRST) {
      return (
        <Box maxWidth={310}>
          <Box alignItems="center" display="flex" marginBottom={6}>
            <Checkbox checked={checked} id="notif" onChange={this.onCheck} size="sm" />
            <Label htmlFor="notif">
              <Box paddingX={2}>
                <Text size="sm">
                  {i18n._(
                    'Want to get recommendations and updates in your inbox?',
                    'checkbox option in a prompt',
                  )}
                </Text>
              </Box>
            </Label>
          </Box>
          <TextField
            id="email"
            type="email"
            onChange={this.onTextChange}
            value={newEmail}
            errorMessage={errorMessage}
          />
          <Box paddingY={1}>
            <Button
              color="red"
              text={i18n._('Done', 'done updating email address')}
              onClick={this.updateEmailAddress}
            />
          </Box>
        </Box>
      );
    } else {
      return (
        <Text overflow="normal" size="lg">
          {interpolateNamedTemplate(
            i18n._(
              'You can always change them later from your {{settingsLink}}.',
              'link to change email settings',
            ),
            {
              settingsLink: (
                <NavigateLink key="settingsLink" to={'/settings/'}>
                  {i18n._('Settings', 'link to edit account settings')}
                </NavigateLink>
              ),
            },
          )}
        </Text>
      );
    }
  }

  render() {
    const { handleDismiss } = this.props;
    const { currentStep } = this.state;

    return (
      <Box color="white" overflow="hidden" shape="rounded" width={660} height={540}>
        <Box display="flex" alignItems="center">
          <Box display="flex" alignItems="end" justifyContent="center" width={330} height={540}>
            <Box width={292} height={479} marginBottom={-6}>
              <Image
                alt={i18n._('preview of mobile app', 'prompt to fix email')}
                naturalWidth={292}
                naturalHeight={479}
                src={fixEmailImage}
              />
            </Box>
          </Box>
          <Box position="relative">
            <Box display="flex" alignItems="center" width={330} height={540}>
              <Box paddingX={3}>
                <Box paddingY={6}>
                  <Text bold overflow="normal" size="xl">
                    {this.getModalText(currentStep)}
                  </Text>
                </Box>
                {this.renderStep(currentStep)}
              </Box>
            </Box>
            {currentStep === STEP.FIRST && (
              <Box padding={6} position="absolute" bottom right>
                <Touchable onTouch={handleDismiss} fullWidth={false}>
                  <Text bold>{i18n._('Skip for now', 'Skip prompt to change email address')}</Text>
                </Touchable>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  }
}

export default FixEmailModal;
